import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  Announcements,
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DroppableContainer,
  ScreenReaderInstructions,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { BOARD_COLUMN_WIDTH } from './board-layout';
import { columnDragId, isColumnDragId } from './drag-ids';

interface BoardCanvasProps {
  children: ReactNode;
  columnIds: string[];
  onDragEnd: (event: DragEndEvent) => void;
  onDragStart?: (event: DragStartEvent) => void;
  onDragOver?: (event: DragOverEvent) => void;
  onDragCancel?: () => void;
  /**
   * Optional resolver from a drag id (e.g. `card:abc` or `column:xyz`) to a
   * human-readable label used in screen-reader announcements.
   */
  getItemLabel?: (dragId: string) => string;
  /**
   * Renders a "+ column" tile at the right edge of the canvas. Hidden when
   * undefined (used by non-member views or contexts that don't allow adding).
   */
  onAddColumn?: () => void;
  /**
   * Renders the floating preview that follows the cursor during a drag.
   * Called with the active dnd-kit drag id; return null to skip preview
   * for a given type.
   */
  renderDragOverlay?: (dragId: string) => ReactNode;
}

// When a column is being dragged, only other columns are valid drop
// targets; a card drag keeps every droppable (needs cards + columns).
export function droppablesForActiveDrag(
  activeId: string,
  containers: DroppableContainer[]
): DroppableContainer[] {
  if (isColumnDragId(activeId)) {
    return containers.filter((container) => isColumnDragId(container.id));
  }

  return containers;
}

const collisionDetection: CollisionDetection = (args) =>
  closestCorners({
    ...args,
    droppableContainers: droppablesForActiveDrag(
      String(args.active.id),
      args.droppableContainers
    ),
  });

const screenReaderInstructions: ScreenReaderInstructions = {
  draggable:
    'To pick up a card or column, press space. While dragging, use arrow keys to move it between positions or columns. Press space again to drop, or escape to cancel and return to the original position.',
};

export function BoardCanvas({
  children,
  columnIds,
  onDragEnd,
  onDragStart,
  onDragOver,
  onDragCancel,
  getItemLabel,
  onAddColumn,
  renderDragOverlay,
}: BoardCanvasProps) {
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
    onDragStart?.(event);
  };
  const handleDragEndInternal = (event: DragEndEvent) => {
    setActiveDragId(null);
    onDragEnd(event);
  };
  const handleDragCancel = () => {
    setActiveDragId(null);
    onDragCancel?.();
  };
  const sensors = useSensors(
    // distance: 5 lets a card's project-open onClick still fire on
    // simple click (drag only activates after 5px of movement).
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const announcements: Announcements = useMemo(
    () => ({
      onDragStart({ active }) {
        const label = getItemLabel?.(String(active.id)) ?? String(active.id);
        return `Picked up ${label}.`;
      },
      onDragOver({ active, over }) {
        if (!over) return;
        const a = getItemLabel?.(String(active.id)) ?? String(active.id);
        const o = getItemLabel?.(String(over.id)) ?? String(over.id);
        return `${a} is over ${o}.`;
      },
      onDragEnd({ active, over }) {
        const a = getItemLabel?.(String(active.id)) ?? String(active.id);
        if (over) {
          const o = getItemLabel?.(String(over.id)) ?? String(over.id);
          return `${a} was dropped over ${o}.`;
        }
        return `${a} was dropped.`;
      },
      onDragCancel({ active }) {
        const label = getItemLabel?.(String(active.id)) ?? String(active.id);
        return `${label} drag was cancelled — returned to original position.`;
      },
    }),
    [getItemLabel]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={onDragOver}
      onDragEnd={handleDragEndInternal}
      onDragCancel={handleDragCancel}
      accessibility={{ announcements, screenReaderInstructions }}
    >
      <SortableContext
        items={columnIds.map(columnDragId)}
        strategy={horizontalListSortingStrategy}
      >
        <Box
          data-testid="board-canvas-scroll"
          sx={{
            overflowX: 'auto',
            scrollSnapType: 'none',
            py: 2,
            px: 0,
            scrollbarWidth: 'thin',
            minHeight: { xs: 0, md: 460 },
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="stretch"
            sx={{ height: '100%' }}
          >
            {children}
            {onAddColumn && (
              <ButtonBase
                onClick={onAddColumn}
                aria-label="add column"
                sx={{
                  minWidth: BOARD_COLUMN_WIDTH,
                  width: BOARD_COLUMN_WIDTH,
                  height: 96,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  color: 'text.secondary',
                  alignSelf: 'flex-start',
                  flexDirection: 'column',
                  gap: 0.5,
                  scrollSnapAlign: 'start',
                  '&:hover': {
                    borderColor: 'text.secondary',
                    color: 'text.primary',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <AddIcon />
                <Typography variant="body2">Add column</Typography>
              </ButtonBase>
            )}
          </Stack>
        </Box>
      </SortableContext>
      <DragOverlay dropAnimation={{ duration: 180, easing: 'ease-out' }}>
        {activeDragId && renderDragOverlay
          ? renderDragOverlay(activeDragId)
          : null}
      </DragOverlay>
    </DndContext>
  );
}
